import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, 
  Calendar as CalendarIcon, 
  Clock, 
  User, 
  FileText, 
  ChevronDown, 
  ChevronUp, 
  ChevronLeft,
  ChevronRight,
  RotateCcw, 
  Edit, 
  Gift, 
  Mail, 
  Printer, 
  Slash,
  Download,
  Filter,
  X,
  Loader2,
  MoreHorizontal,
  CreditCard,
  ArrowRight,
  Trash2,
  MessageCircle,
  Package
} from 'lucide-react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  addDoc,
  serverTimestamp,
  limit,
  handleFirestoreError,
  OperationType
} from '../firebase';
import { db, auth } from '../firebase';
import { cn } from '../lib/utils';
import { localizePrintableHtml, localizePdfText, getLocale } from '../lib/localization';
import { notify } from '../services/notify';
import HistoryNotesView from './HistoryNotesView';
import { logHistory } from '../services/historyService';

interface POSSale {
  id: string;
  number: string;
  date: string;
  time: string;
  contactName: string;
  contactId: string;
  contactPhone?: string;
  totalAmount: number;
  status: string;
  items: any[];
  subtotal: number;
  totalTax: number;
  orderDiscount: number;
  notes: string;
  createdByName: string;
  createdBy: string;
  createdByEmail?: string;
  currency: string;
  createdAt: any;
  paymentMethod?: string;
  fulfillmentStatus?: string;
  storeId?: string;
}

function Calendar({ selectedDate, onSelect, className }: { selectedDate: string, onSelect: (date: string) => void, className?: string }) {
  const [currentMonth, setCurrentMonth] = useState(selectedDate ? new Date(selectedDate) : new Date());
  
  const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();
  
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const days = daysInMonth(year, month);
  const firstDay = firstDayOfMonth(year, month);
  
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  
  const prevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));
  
  const isSelected = (day: number) => {
    if (!selectedDate) return false;
    const d = new Date(selectedDate);
    return d.getDate() === day && d.getMonth() === month && d.getFullYear() === year;
  };

  return (
    <div className={cn("p-4 bg-white rounded-xl w-64", className)}>
      <div className="flex items-center justify-between mb-4">
        <button onClick={prevMonth} className="p-1 hover:bg-black/5 rounded-full"><ChevronLeft className="w-4 h-4" /></button>
        <div className="text-sm font-bold">{monthNames[month]} {year}</div>
        <button onClick={nextMonth} className="p-1 hover:bg-black/5 rounded-full"><ChevronRight className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
          <div key={d} className="text-[10px] font-bold opacity-40 text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} />)}
        {Array.from({ length: days }).map((_, i) => {
          const day = i + 1;
          return (
            <button
              key={day}
              onClick={() => {
                const d = new Date(year, month, day);
                onSelect(d.toISOString().split('T')[0]);
              }}
              className={cn(
                "w-8 h-8 text-xs rounded-lg transition-all flex items-center justify-center",
                isSelected(day) ? "bg-[#141414] text-white font-bold" : "hover:bg-black/5"
              )}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function POSSalesHistoryView({ 
  orgId, 
  selectedStoreId,
  setSelectedStoreId,
  onContinueSale,
  onReturnSale,
  language = 'en'
}: { 
  orgId: string, 
  selectedStoreId: string,
  setSelectedStoreId: (id: string) => void,
  onContinueSale?: (saleId: string) => void,
  onReturnSale?: (saleId: string) => void,
  language?: string
}) {
  const [sales, setSales] = useState<POSSale[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedSaleId, setExpandedSaleId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'return' | 'continue'>('all');
  const [editingSale, setEditingSale] = useState<POSSale | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [chartOfAccounts, setChartOfAccounts] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  
  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('00:00');
  const [endTime, setEndTime] = useState('23:59');
  const [useTimeRange, setUseTimeRange] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [receiptSearch, setReceiptSearch] = useState('');
  const [soldByFilter, setSoldByFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fulfillmentStatusFilter, setFulfillmentStatusFilter] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [orgUsers, setOrgUsers] = useState<any[]>([]);

  // Store Selection States
  const [stores, setStores] = useState<any[]>([]);

  useEffect(() => {
    if (!orgId) return;

    const localToken = typeof window !== 'undefined' ? localStorage.getItem('app_auth_token') : null;
    if (localToken) {
      let cancelled = false;
      fetch(`/api/organizations/${orgId}/stores`, { headers: { Authorization: `Bearer ${localToken}` } })
        .then(r => r.json())
        .then(data => {
          if (cancelled) return;
          const activeStores = Array.isArray(data) ? data.filter((s: any) => s.status === 'active') : [];
          setStores(activeStores);
          if (activeStores.length === 1 && !selectedStoreId) setSelectedStoreId(activeStores[0].id);
        })
        .catch(err => { if (!cancelled) console.error('POS history stores load failed:', err); });
      return () => { cancelled = true; };
    }
    const q = query(
      collection(db, 'stores'),
      where('orgId', '==', orgId),
      where('status', '==', 'active')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const storesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStores(storesData);
      
      // Auto-select if only one store
      if (storesData.length === 1 && !selectedStoreId) {
        setSelectedStoreId(storesData[0].id);
      }
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'stores'));
    return () => unsubscribe();
  }, [orgId]);

  const salespeople = React.useMemo(() => {
    const names = new Set<string>();
    sales.forEach(sale => {
      const name = sale.createdByName || sale.createdByEmail || 'Unknown';
      if (name !== 'Unknown') names.add(name);
    });
    return Array.from(names).sort();
  }, [sales]);

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);

  const datePickerRef = useRef<HTMLDivElement>(null);
  const timePickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
      if (timePickerRef.current && !timePickerRef.current.contains(event.target as Node)) {
        setShowTimePicker(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const unsubscribeRef = useRef<(() => void) | null>(null);

  const fetchSales = () => {
    if (!orgId) return;

    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }

    setLoading(true);

    const localAuthToken = typeof window !== 'undefined'
      ? localStorage.getItem('app_auth_token')
      : null;

    if (localAuthToken) {
      let cancelled = false;

      const fetchLocalSales = async () => {
        try {
          const response = await fetch(`/api/organizations/${orgId}/pos-sales`, {
            headers: { Authorization: `Bearer ${localAuthToken}` }
          });

          if (!response.ok) {
            throw new Error('Failed to load POS sales');
          }

          const payload = await response.json();
          const localSales = Array.isArray(payload) ? payload : payload.sales || payload.data || [];

          if (!cancelled) {
            setSales(localSales.map((sale: any) => ({
              id: sale.id,
              ...(sale.data || sale)
            })) as POSSale[]);
          }
        } catch (error) {
          console.error('Error fetching local POS sales:', error);
          if (!cancelled) {
            notify.error('Failed to load POS sales');
          }
        } finally {
          if (!cancelled) {
            setLoading(false);
          }
        }
      };

      fetchLocalSales();
      return () => {
        cancelled = true;
      };
    }

    let q = query(
      collection(db, 'documents'),
      where('orgId', '==', orgId),
      where('source', '==', 'POS'),
      orderBy('createdAt', 'desc')
    );

    if (selectedStoreId) {
      q = query(q, where('storeId', '==', selectedStoreId));
    }

    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      q = query(q, where('createdAt', '>=', start));
    }
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      q = query(q, where('createdAt', '<=', end));
    }

    // If no date range, limit to 50
    if (!startDate && !endDate) {
      q = query(q, limit(50));
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const salesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as POSSale[];
      setSales(salesData);
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'documents');
      setLoading(false);
    });

    return unsubscribe;
  };

  useEffect(() => {
    if (!orgId) return;

    const localTokenSec = typeof window !== 'undefined' ? localStorage.getItem('app_auth_token') : null;
    if (localTokenSec) {
      let cancelled = false;
      const headers = { Authorization: `Bearer ${localTokenSec}` };
      Promise.all([
        fetch(`/api/organizations/${orgId}/bank-accounts`, { headers }).then(r => r.json()),
        fetch(`/api/organizations/${orgId}/chart-of-accounts`, { headers }).then(r => r.json()),
        fetch(`/api/organizations/${orgId}/contacts`, { headers }).then(r => r.json()),
        fetch(`/api/organizations/${orgId}/users`, { headers }).then(r => r.json()),
      ]).then(([banks, coa, contacts, users]) => {
        if (cancelled) return;
        setBankAccounts(Array.isArray(banks) ? banks : []);
        setChartOfAccounts(Array.isArray(coa) ? coa : []);
        const customers = (Array.isArray(contacts) ? contacts : [])
          .filter((c: any) => c.status?.toLowerCase() === 'active' && ['customer','both'].includes(c.type?.toLowerCase()))
          .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
        setContacts(customers);
        const uniqueUsers = Array.from(new Map((Array.isArray(users) ? users : []).map((u: any) => [u.id, u])).values())
          .sort((a: any, b: any) => (a.displayName || a.fullName || '').localeCompare(b.displayName || b.fullName || ''));
        setOrgUsers(uniqueUsers);
      }).catch(err => { if (!cancelled) console.error('POS history secondary load failed:', err); });
      return () => { cancelled = true; };
    }

    // Fetch bank accounts
    const bankQuery = query(
      collection(db, 'bank_accounts'), 
      where('orgId', '==', orgId),
      orderBy('accountName', 'asc')
    );
    const unsubscribeBank = onSnapshot(bankQuery, (snapshot) => {
      setBankAccounts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'bank_accounts'));

    // Fetch chart of accounts
    const coaQuery = query(
      collection(db, 'chart_of_accounts'),
      where('orgId', '==', orgId),
      where('status', '==', 'active'),
      orderBy('code', 'asc')
    );
    const unsubscribeCOA = onSnapshot(coaQuery, (snapshot) => {
      setChartOfAccounts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'chart_of_accounts'));

    // Fetch contacts
    const contactsQuery = query(
      collection(db, 'contacts'), 
      where('orgId', '==', orgId)
    );
    const unsubscribeContacts = onSnapshot(contactsQuery, (snapshot) => {
      const docs = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter((c: any) => 
          (c.status?.toLowerCase() === 'active') && 
          (c.type?.toLowerCase() === 'customer' || c.type?.toLowerCase() === 'both')
        )
        .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
      setContacts(docs);
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'contacts'));

    // Fetch organization users (using org_users since root users collection is restricted)
    const usersQuery = query(
      collection(db, 'org_users'),
      where('orgId', '==', orgId)
    );
    const unsubscribeUsers = onSnapshot(usersQuery, (snapshot) => {
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Filter out duplicates and sort
      const uniqueUsers = Array.from(new Map(docs.map(u => [u.id, u])).values())
        .sort((a: any, b: any) => (a.displayName || a.name || '').localeCompare(b.displayName || b.name || ''));
      setOrgUsers(uniqueUsers);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'org_users');
    });

    return () => {
      unsubscribeBank();
      unsubscribeCOA();
      unsubscribeContacts();
      unsubscribeUsers();
    };
  }, [orgId]);

  useEffect(() => {
    fetchSales();
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [orgId, selectedStoreId, startDate, endDate]);

  const filteredSales = sales.filter(sale => {
    // Tab filtering
    if (activeTab === 'continue' && sale.status !== 'Draft' && sale.status !== 'Parked' && sale.status !== 'Awaiting Payment') return false;
    if (activeTab === 'return' && sale.status !== 'Paid' && sale.status !== 'Completed') return false;

    const matchesCustomer = (sale.contactName || 'Walk-in Customer').toLowerCase().includes(customerSearch.toLowerCase()) ||
                          (sale.contactPhone || '').toLowerCase().includes(customerSearch.toLowerCase());
    const matchesReceipt = sale.number.toLowerCase().includes(receiptSearch.toLowerCase()) || 
                          (sale.notes && sale.notes.toLowerCase().includes(receiptSearch.toLowerCase()));
    
    const matchesSoldBy = !soldByFilter || sale.createdByName === soldByFilter;
    
    const matchesStatus = !statusFilter || (() => {
      const isReturn = sale.number?.toLowerCase().includes('/return');
      if (statusFilter === 'Paid') return sale.status === 'Paid' && !isReturn;
      if (statusFilter === 'Returned/Paid') return sale.status === 'Paid' && isReturn;
      if (statusFilter === 'Returned/Awaiting payment') return (sale.status === 'Awaiting Payment' || sale.status === 'Parked') && isReturn;
      if (statusFilter === 'Parked') return (sale.status === 'Awaiting Payment' || sale.status === 'Parked') && !isReturn;
      if (statusFilter === 'Sales') return sale.status === 'Paid' || sale.status === 'Completed';
      if (statusFilter === 'Voided') return sale.status === 'Voided';
      return sale.status === statusFilter;
    })();

    let matchesTime = true;
    if (useTimeRange) {
      try {
        const saleDate = sale.createdAt?.toDate?.() || new Date(sale.createdAt);
        const saleTimeStr = `${saleDate.getHours().toString().padStart(2, '0')}:${saleDate.getMinutes().toString().padStart(2, '0')}`;
        matchesTime = saleTimeStr >= startTime && saleTimeStr <= endTime;
      } catch (e) {
        matchesTime = false;
      }
    }

    const matchesFulfillmentStatus = !fulfillmentStatusFilter || (sale.fulfillmentStatus || 'pending') === fulfillmentStatusFilter;

    // Amount Filters
    const matchesMinAmount = !minAmount || (sale.totalAmount || 0) >= parseFloat(minAmount);
    const matchesMaxAmount = !maxAmount || (sale.totalAmount || 0) <= parseFloat(maxAmount);
    
    // Tag Filter
    const matchesTag = !tagFilter || ((sale as any).tags && (sale as any).tags.includes(tagFilter));

    return matchesCustomer && matchesReceipt && matchesTime && matchesSoldBy && matchesStatus && matchesFulfillmentStatus && matchesMinAmount && matchesMaxAmount && matchesTag;
  });

  const formatTime12h = (timeStr: string) => {
    if (!timeStr) return '';
    const [h, m] = timeStr.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
  };

  const handleReturn = async (sale: POSSale) => {
    const localAuthToken = typeof window !== 'undefined'
      ? localStorage.getItem('app_auth_token')
      : null;

    if (localAuthToken) {
      const storeId = sale.storeId || selectedStoreId;

      if (!storeId) {
        notify.error('A store is required to return this sale');
        return;
      }

      try {
        const response = await fetch(`/api/organizations/${orgId}/pos-sales/${sale.id}/returns`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localAuthToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            storeId,
            reason: 'Returned from POS sales history',
            items: sale.items.map((item: any) => ({
              itemId: item.itemId || item.productId || item.id,
              ...(item.lotId ? { lotId: item.lotId } : {}),
              quantity: item.quantity
            }))
          })
        });

        if (!response.ok) {
          throw new Error('Failed to return sale');
        }

        const refundJournalResponse = await fetch(
          `/api/organizations/${orgId}/pos-sales/${sale.id}/refund-journal`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${localAuthToken}` }
          }
        );
        const refundJournalResult = await refundJournalResponse.json().catch(() => null);
        const alreadyPosted = refundJournalResult?.alreadyPosted === true;

        if (!refundJournalResponse.ok && !alreadyPosted) {
          throw new Error('Failed to post refund journal');
        }

        setSales(currentSales => currentSales.map(currentSale =>
          currentSale.id === sale.id
            ? { ...currentSale, status: 'Returned', fulfillmentStatus: 'returned' }
            : currentSale
        ));
        notify.confirm(
          alreadyPosted
            ? 'Sale returned successfully; refund journal was already posted'
            : 'Sale returned successfully'
        );
      } catch (error) {
        console.error('Error returning local POS sale:', error);
        notify.error('Failed to return sale');
      }

      return;
    }

    onReturnSale?.(sale.id);
  };

  const handlePrintReceipt = async (sale: POSSale, isGift: boolean = false) => {
    let storeData: any = null;
    if (sale.storeId) {
      try {
        const localToken = typeof window !== 'undefined' ? localStorage.getItem('app_auth_token') : null;
        if (localToken) {
          storeData = stores.find(s => s.id === sale.storeId) || null;
        } else {
          const storeDoc = await getDoc(doc(db, 'stores', sale.storeId));
          if (storeDoc.exists()) {
            storeData = storeDoc.data();
          }
        }
      } catch (error) {
        console.error("Error fetching store data for receipt:", error);
      }
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      notify.error('Please allow popups to print receipts');
      return;
    }

    const itemsHtml = sale.items.map(item => `
      <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
        <div>
          <div>${item.name || item.description}</div>
          <div style="font-size: 12px; color: #666;">Qty: ${item.quantity}${isGift ? '' : ' x ' + sale.currency + ' ' + (item.unitPrice || item.price || 0).toFixed(2)}</div>
        </div>
        <div>${isGift ? '' : sale.currency + ' ' + ((item.unitPrice || item.price || 0) * item.quantity).toFixed(2)}</div>
      </div>
    `).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt ${sale.number}</title>
        <style>
          body { font-family: monospace; padding: 20px; max-width: 300px; margin: 0 auto; color: #141414; }
          .header { text-align: center; margin-bottom: 20px; }
          .logo { max-width: 100px; max-height: 100px; margin-bottom: 10px; }
          .divider { border-top: 1px dashed #000; margin: 10px 0; }
          .total { font-weight: bold; font-size: 1.2em; margin-top: 10px; }
          .footer { text-align: center; margin-top: 20px; font-size: 12px; }
          .terms { margin-top: 15px; font-size: 10px; color: #666; border-top: 1px solid #eee; pt: 10px; }
          @media print {
            body { padding: 0; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          ${storeData?.logoUrl ? `<img src="${storeData.logoUrl}" class="logo" />` : ''}
          <div style="font-weight: bold; font-size: 1.2em;">${storeData?.name || 'STORE RECEIPT'}</div>
          ${storeData?.address ? `<div style="font-size: 12px;">${storeData.address}</div>` : ''}
          ${storeData?.socialMediaLink ? `<div style="font-size: 12px; color: #666;">${storeData.socialMediaLink}</div>` : ''}
          <div class="divider"></div>
          <h2>${isGift ? 'GIFT RECEIPT' : 'RECEIPT'}</h2>
          <div>Order #${sale.number}</div>
          <div>${new Date(sale.createdAt?.toDate?.() || sale.createdAt).toLocaleString()}</div>
        </div>
        <div class="divider"></div>
        ${itemsHtml}
        <div class="divider"></div>
        ${!isGift ? `
          <div style="display: flex; justify-content: space-between;">
            <span>Subtotal</span>
            <span>${sale.currency} ${(sale.subtotal || 0).toFixed(2)}</span>
          </div>
          <div style="display: flex; justify-content: space-between;">
            <span>Tax</span>
            <span>${sale.currency} ${(sale.totalTax || 0).toFixed(2)}</span>
          </div>
          <div style="display: flex; justify-content: space-between;" class="total">
            <span>Total</span>
            <span>${sale.currency} ${(sale.totalAmount || 0).toFixed(2)}</span>
          </div>
        ` : `
          <div style="text-align: center; margin-top: 20px;">
            <strong>Gift Receipt</strong><br/>
            No prices shown
          </div>
        `}
        <div class="divider"></div>
        <div class="footer">
          ${storeData?.termsAndConditions ? `<div class="terms">${storeData.termsAndConditions}</div>` : ''}
        </div>
        <script>
          window.onload = () => {
            window.print();
            setTimeout(() => window.close(), 500);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(localizePrintableHtml(html));
    printWindow.document.close();
  };

  const generateReceiptText = (sale: POSSale, isGift: boolean = false) => {
    let text = `${isGift ? 'Gift ' : ''}Receipt #${sale.number}\n`;
    text += `Date: ${new Date(sale.createdAt?.toDate?.() || sale.createdAt).toLocaleString(getLocale())}\n\n`;
    text += `Items:\n`;
    sale.items.forEach(item => {
      text += `- ${item.name} (Qty: ${item.quantity})`;
      if (!isGift) {
        text += ` - ${sale.currency} ${((item.price || 0) * item.quantity).toFixed(2)}`;
      }
      text += `\n`;
    });
    
    if (!isGift) {
      text += `\nSubtotal: ${sale.currency} ${(sale.subtotal || 0).toFixed(2)}\n`;
      text += `Tax: ${sale.currency} ${(sale.totalTax || 0).toFixed(2)}\n`;
      text += `Total: ${sale.currency} ${(sale.totalAmount || 0).toFixed(2)}\n`;
    }
    
    text += `\nThank you for your business!`;
    return encodeURIComponent(text.split('\n').map(line => localizePdfText(line)).join('\n'));
  };

  const handleEmailReceipt = (sale: POSSale) => {
    const subject = encodeURIComponent(localizePdfText(`Receipt #${sale.number}`));
    const body = generateReceiptText(sale);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const handleWhatsAppReceipt = (sale: POSSale) => {
    const text = generateReceiptText(sale);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const handleVoid = async (saleId: string) => {
    try {
      const localToken = typeof window !== 'undefined' ? localStorage.getItem('app_auth_token') : null;
      if (localToken) {
        const res = await fetch(`/api/organizations/${orgId}/pos-sales/${saleId}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${localToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'Voided' }),
        });
        if (!res.ok) throw new Error('Failed to void sale');
        setSales(prev => prev.map(s => s.id === saleId ? { ...s, status: 'Voided' } : s));
      } else {
        await updateDoc(doc(db, 'documents', saleId), {
          status: 'Voided',
          updatedAt: serverTimestamp()
        });
      }
      notify.confirm("Sale voided successfully");
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'documents');
      notify.error("Failed to void sale");
    }
  };

  const handleUpdateSale = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSale) return;

    setIsSaving(true);
    try {
      const localToken = typeof window !== 'undefined' ? localStorage.getItem('app_auth_token') : null;
      if (localToken) {
        const res = await fetch(`/api/organizations/${orgId}/pos-sales/${editingSale.id}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${localToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            notes: editingSale.notes,
            contactName: editingSale.contactName,
            contactId: editingSale.contactId,
            paymentMethod: editingSale.paymentMethod,
          }),
        });
        if (!res.ok) throw new Error('Failed to update sale');
        setSales(prev => prev.map(s => s.id === editingSale.id ? { ...s, ...editingSale } : s));
      } else {
        const saleRef = doc(db, 'documents', editingSale.id);
        await updateDoc(saleRef, {
          notes: editingSale.notes,
          contactName: editingSale.contactName,
          contactId: editingSale.contactId,
          paymentMethod: editingSale.paymentMethod,
          updatedAt: serverTimestamp()
        });
        await logHistory(orgId, 'Updated POS Sale', `POS Sale #${editingSale.number} updated in history.`, auth.currentUser, editingSale.id, 'Invoice');
      }
      notify.confirm("Sale updated successfully");
      setEditingSale(null);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'documents');
      notify.error("Failed to update sale");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold tracking-tight italic serif">Sales history</h1>
        {stores.length > 0 && (
          <select
            value={selectedStoreId}
            onChange={(e) => setSelectedStoreId(e.target.value)}
            className="px-4 py-2 bg-white border border-[#141414]/10 rounded-xl text-sm font-bold focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="">All Stores</option>
            {stores.map(store => (
              <option key={store.id} value={store.id}>{store.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-8 border-b border-[#141414]/10">
        <button 
          onClick={() => setActiveTab('all')}
          className={cn(
            "pb-4 text-sm font-bold transition-all relative",
            activeTab === 'all' ? "text-[#141414]" : "opacity-40 hover:opacity-100"
          )}
        >
          All
          {activeTab === 'all' && <div className="absolute bottom-0 left-0 w-full h-1 bg-primary rounded-full" />}
        </button>
        <button 
          onClick={() => setActiveTab('return')}
          className={cn(
            "pb-4 text-sm font-bold transition-all relative",
            activeTab === 'return' ? "text-[#141414]" : "opacity-40 hover:opacity-100"
          )}
        >
          Proceed Returns
          {activeTab === 'return' && <div className="absolute bottom-0 left-0 w-full h-1 bg-primary rounded-full" />}
        </button>
        <button 
          onClick={() => setActiveTab('continue')}
          className={cn(
            "pb-4 text-sm font-bold transition-all relative",
            activeTab === 'continue' ? "text-[#141414]" : "opacity-40 hover:opacity-100"
          )}
        >
          Continuous Sell
          {activeTab === 'continue' && <div className="absolute bottom-0 left-0 w-full h-1 bg-primary rounded-full" />}
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="space-y-2 relative" ref={datePickerRef}>
          <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Date</label>
          <div className="relative">
            <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40" />
            <input 
              type="text" 
              readOnly
              placeholder="Choose date range..."
              value={startDate && endDate ? `${startDate} to ${endDate}` : startDate || endDate || ''}
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="w-full bg-white border border-[#141414]/10 pl-10 pr-4 py-3 rounded-xl text-sm outline-none focus:ring-2 ring-primary/20 transition-all cursor-pointer"
            />
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40" />
          </div>

          {showDatePicker && (
            <div className="absolute top-full left-0 mt-2 p-6 bg-white rounded-[2.5rem] shadow-2xl border border-[#141414]/10 z-50 flex flex-col md:flex-row gap-8 animate-in fade-in zoom-in-95 duration-200">
              <div className="space-y-4">
                <h4 className="text-[10px] font-bold uppercase tracking-widest opacity-40">From</h4>
                <Calendar selectedDate={startDate} onSelect={setStartDate} />
              </div>
              <div className="hidden md:block w-px bg-[#141414]/5" />
              <div className="space-y-4">
                <h4 className="text-[10px] font-bold uppercase tracking-widest opacity-40">To</h4>
                <Calendar selectedDate={endDate} onSelect={setEndDate} />
              </div>
              <div className="flex flex-col justify-end gap-4">
                <button 
                  onClick={() => { setStartDate(''); setEndDate(''); }}
                  className="text-[10px] font-bold uppercase tracking-widest opacity-40 hover:opacity-100 transition-opacity"
                >
                  Clear dates
                </button>
                <button 
                  onClick={() => setShowDatePicker(false)}
                  className="bg-primary text-white px-6 py-2 rounded-xl font-bold text-xs shadow-lg shadow-primary/20 hover:opacity-90 transition-all"
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2 relative" ref={timePickerRef}>
          <div className="flex items-center gap-2">
            <input 
              type="checkbox" 
              id="useTimeRange"
              checked={useTimeRange}
              onChange={(e) => setUseTimeRange(e.target.checked)}
              className="w-3 h-3 accent-primary"
            />
            <label htmlFor="useTimeRange" className="text-[10px] font-bold uppercase tracking-widest opacity-40 cursor-pointer">Time range</label>
          </div>
          <div className="relative">
            <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40" />
            <input 
              type="text" 
              readOnly
              placeholder="Choose a time range"
              value={useTimeRange ? `${formatTime12h(startTime)} - ${formatTime12h(endTime)}` : ''}
              onClick={() => useTimeRange && setShowTimePicker(!showTimePicker)}
              className={cn(
                "w-full bg-white border border-[#141414]/10 pl-10 pr-4 py-3 rounded-xl text-sm outline-none focus:ring-2 ring-primary/20 transition-all",
                useTimeRange ? "cursor-pointer" : "opacity-50 cursor-not-allowed"
              )}
            />
          </div>

          {showTimePicker && useTimeRange && (
            <div className="absolute top-full left-0 mt-2 p-6 bg-white rounded-[2.5rem] shadow-2xl border border-[#141414]/10 z-50 space-y-6 animate-in fade-in zoom-in-95 duration-200">
              <div className="flex gap-8">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">From</label>
                  <div className="flex gap-1">
                    <select 
                      value={startTime.split(':')[0]} 
                      onChange={(e) => setStartTime(`${e.target.value}:${startTime.split(':')[1]}`)}
                      className="bg-[#141414]/5 border-none rounded-lg px-2 py-1 text-xs font-bold outline-none"
                    >
                      {Array.from({ length: 24 }).map((_, i) => {
                        const label = i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`;
                        return <option key={i} value={i.toString().padStart(2, '0')}>{label}</option>;
                      })}
                    </select>
                    <span className="opacity-40">:</span>
                    <select 
                      value={startTime.split(':')[1]} 
                      onChange={(e) => setStartTime(`${startTime.split(':')[0]}:${e.target.value}`)}
                      className="bg-[#141414]/5 border-none rounded-lg px-2 py-1 text-xs font-bold outline-none"
                    >
                      {Array.from({ length: 60 }).map((_, i) => (
                        <option key={i} value={i.toString().padStart(2, '0')}>{i.toString().padStart(2, '0')}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">To</label>
                  <div className="flex gap-1">
                    <select 
                      value={endTime.split(':')[0]} 
                      onChange={(e) => setEndTime(`${e.target.value}:${endTime.split(':')[1]}`)}
                      className="bg-[#141414]/5 border-none rounded-lg px-2 py-1 text-xs font-bold outline-none"
                    >
                      {Array.from({ length: 24 }).map((_, i) => {
                        const label = i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`;
                        return <option key={i} value={i.toString().padStart(2, '0')}>{label}</option>;
                      })}
                    </select>
                    <span className="opacity-40">:</span>
                    <select 
                      value={endTime.split(':')[1]} 
                      onChange={(e) => setEndTime(`${endTime.split(':')[0]}:${e.target.value}`)}
                      className="bg-[#141414]/5 border-none rounded-lg px-2 py-1 text-xs font-bold outline-none"
                    >
                      {Array.from({ length: 60 }).map((_, i) => (
                        <option key={i} value={i.toString().padStart(2, '0')}>{i.toString().padStart(2, '0')}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-4 items-center">
                <button 
                  onClick={() => { setStartTime('00:00'); setEndTime('23:59'); }}
                  className="text-[10px] font-bold uppercase tracking-widest opacity-40 hover:opacity-100 transition-opacity"
                >
                  Clear times
                </button>
                <button 
                  onClick={() => setShowTimePicker(false)}
                  className="bg-primary text-white px-6 py-2 rounded-xl font-bold text-xs shadow-lg shadow-primary/20 hover:opacity-90 transition-all"
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Customer</label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40" />
            <input 
              type="text" 
              placeholder="Enter a customer"
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              className="w-full bg-white border border-[#141414]/10 pl-10 pr-4 py-3 rounded-xl text-sm outline-none focus:ring-2 ring-primary/20 transition-all"
            />
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Receipt or note</label>
          <div className="relative">
            <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40" />
            <input 
              type="text" 
              placeholder="Enter a receipt or note"
              value={receiptSearch}
              onChange={(e) => setReceiptSearch(e.target.value)}
              className="w-full bg-white border border-[#141414]/10 pl-10 pr-4 py-3 rounded-xl text-sm outline-none focus:ring-2 ring-primary/20 transition-all"
            />
          </div>
        </div>

        {showMoreFilters && (
          <>
            <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
              <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Sold by</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40" />
                <select 
                  value={soldByFilter}
                  onChange={(e) => setSoldByFilter(e.target.value)}
                  className="w-full bg-white border border-[#141414]/10 pl-10 pr-4 py-3 rounded-xl text-sm outline-none focus:ring-2 ring-primary/20 transition-all appearance-none"
                >
                  <option value="">All Salespeople</option>
                  {salespeople.map(name => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40 pointer-events-none" />
              </div>
            </div>
            <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
              <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Status</label>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40" />
                <select 
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full bg-white border border-[#141414]/10 pl-10 pr-4 py-3 rounded-xl text-sm outline-none focus:ring-2 ring-primary/20 transition-all appearance-none"
                >
                  <option value="">All Statuses</option>
                  <option value="Paid">Paid</option>
                  <option value="Returned/Paid">Return and Paid</option>
                  <option value="Sales">Sales (All Paid)</option>
                  <option value="Returned/Awaiting payment">Awaiting (Return)</option>
                  <option value="Parked">Parked</option>
                  <option value="Voided">Voided</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40 pointer-events-none" />
              </div>
            </div>
            <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
              <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Fulfillment Status</label>
              <div className="relative">
                <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40" />
                <select 
                  value={fulfillmentStatusFilter}
                  onChange={(e) => setFulfillmentStatusFilter(e.target.value)}
                  className="w-full bg-white border border-[#141414]/10 pl-10 pr-4 py-3 rounded-xl text-sm outline-none focus:ring-2 ring-primary/20 transition-all appearance-none"
                >
                  <option value="">All Fulfillment Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="in progress">In Progress</option>
                  <option value="ready to pick up">Ready to Pick Up</option>
                  <option value="out for delivery">Out for Delivery</option>
                  <option value="delivered">Delivered</option>
                  <option value="canceled">Canceled</option>
                  <option value="returned">Returned</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40 pointer-events-none" />
              </div>
            </div>
            <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
              <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Total Amount</label>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" value={minAmount} onChange={e => setMinAmount(e.target.value)} placeholder="Min" className="w-full bg-white border border-[#141414]/10 px-3 py-3 rounded-xl text-xs font-bold outline-none ring-primary/20 focus:ring-2" />
                <input type="number" value={maxAmount} onChange={e => setMaxAmount(e.target.value)} placeholder="Max" className="w-full bg-white border border-[#141414]/10 px-3 py-3 rounded-xl text-xs font-bold outline-none ring-primary/20 focus:ring-2" />
              </div>
            </div>
            <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
              <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Tag</label>
              <input 
                type="text" 
                placeholder="Search by tag..."
                value={tagFilter}
                onChange={e => setTagFilter(e.target.value)}
                className="w-full bg-white border border-[#141414]/10 px-4 py-3 rounded-xl text-sm font-bold outline-none ring-primary/20 focus:ring-2"
              />
            </div>
          </>
        )}
      </div>

      <div className="flex items-center justify-end gap-4">
        <button 
          onClick={() => {
            setStartDate('');
            setEndDate('');
            setStartTime('00:00');
            setEndTime('23:59');
            setUseTimeRange(false);
            setCustomerSearch('');
            setReceiptSearch('');
            setSoldByFilter('');
            setStatusFilter('');
            fetchSales();
          }}
          className="text-xs font-bold underline underline-offset-4 opacity-60 hover:opacity-100 transition-opacity"
        >
          Clear filters
        </button>
        <button 
          onClick={() => setShowMoreFilters(!showMoreFilters)}
          className="text-xs font-bold underline underline-offset-4 opacity-60 hover:opacity-100 transition-opacity"
        >
          {showMoreFilters ? 'Less filters' : 'More filters'}
        </button>
        <button 
          onClick={() => fetchSales()}
          className="bg-primary text-white px-8 py-3 rounded-xl font-bold text-sm shadow-lg shadow-primary/20 hover:opacity-90 transition-all"
        >
          Search
        </button>
      </div>

      <div className="flex items-center justify-between px-6 py-2 bg-[#141414]/5 rounded-2xl">
        <p className="text-xs opacity-40 font-bold uppercase tracking-widest">Displaying {filteredSales.length} sales</p>
        <button className="flex items-center gap-2 text-xs font-bold hover:opacity-60 transition-opacity text-primary">
          <Download className="w-4 h-4" />
          <span>Export list</span>
        </button>
      </div>

      {/* Sales List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between px-0 py-2 text-[10px] font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/20">
          <div className="flex-[2] text-center border-r border-[#141414]/20 py-2">Receipt</div>
          <div className="flex-[2] text-center border-r border-[#141414]/20 py-2">Customer</div>
          <div className="flex-[2] text-center border-r border-[#141414]/20 py-2">Sold by</div>
          <div className="flex-[2] text-center border-r border-[#141414]/20 py-2">Store</div>
          <div className="flex-1 text-center border-r border-[#141414]/20 py-2">Sale total</div>
          <div className="flex-1 text-center border-r border-[#141414]/20 py-2 mr-[45px]">Status</div>
          <div className="flex-1 text-center py-2 mb-0 mr-[-41px]">Fulfillment</div>
          <div className="w-24" />
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 opacity-20">
            <Loader2 className="w-12 h-12 animate-spin mb-4" />
            <p className="italic serif">Loading sales history...</p>
          </div>
        ) : filteredSales.length === 0 ? (
          <div className="text-center py-20 opacity-40 italic serif border-2 border-dashed border-[#141414]/10 rounded-[2.5rem]">
            No sales found matching your criteria.
          </div>
        ) : (
          <div className="space-y-2">
            {filteredSales.map((sale) => (
              <div key={sale.id} className="group">
                <div 
                  onClick={() => setExpandedSaleId(expandedSaleId === sale.id ? null : sale.id)}
                    className={cn(
                      "flex items-center px-0 py-0 bg-white border border-[#141414]/10 rounded-3xl cursor-pointer transition-all hover:border-[#141414]/30 overflow-hidden",
                      expandedSaleId === sale.id && "rounded-b-none border-b-0 shadow-xl relative z-10"
                    )}
                >
                    <div className="flex-[2] flex flex-col items-center justify-center gap-1 border-r border-[#141414]/20 pt-[18px] pb-4 min-h-[80px] w-[112.4px]">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-lg bg-[#141414]/5 flex items-center justify-center">
                          {expandedSaleId === sale.id ? <ChevronUp className="w-3 h-3 opacity-40" /> : <ChevronDown className="w-3 h-3 opacity-40" />}
                        </div>
                        <p className="text-sm font-bold">{sale.number}</p>
                      </div>
                      <p className="text-[10px] opacity-40">{(() => {
                        try {
                          return new Date(sale.createdAt?.toDate?.() || sale.createdAt).toLocaleString();
                        } catch (e) {
                          return 'Invalid Date';
                        }
                      })()}</p>
                    </div>
                    <div className="flex-[2] flex flex-col items-center justify-center gap-1 border-r border-[#141414]/20 py-4 min-h-[80px]">
                      <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold">
                        {sale.contactName?.split(' ').map(n => n[0]).join('').toUpperCase() || '??'}
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-bold">{sale.contactName || 'Walk-in Customer'}</p>
                        {sale.contactPhone && <p className="text-[10px] opacity-60 font-medium">{sale.contactPhone}</p>}
                        <p className="text-[10px] opacity-40">{sale.contactId || 'walk-in'}</p>
                      </div>
                    </div>
                    <div className="flex-[2] flex flex-col items-center justify-center gap-1 border-r border-[#141414]/20 py-4 min-h-[80px]">
                      <div className="w-8 h-8 rounded-full bg-[#141414]/10 text-[#141414] flex items-center justify-center text-[10px] font-bold">
                        {sale.createdByName?.split(' ').map(n => n[0]).join('').toUpperCase() || '??'}
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-bold">{sale.createdByName || 'Unknown'}</p>
                      </div>
                    </div>
                    <div className="flex-[2] flex flex-col items-center justify-center text-center border-r border-[#141414]/20 py-4 min-h-[80px]">
                      <p className="text-sm font-bold">{stores.find(s => s.id === sale.storeId)?.name || 'Main Store'}</p>
                      <p className="text-[10px] opacity-40 line-clamp-1 px-2">{sale.notes || '-'}</p>
                    </div>
                    <div className="flex-1 text-center font-mono font-bold border-r border-[#141414]/20 py-4 min-h-[80px] flex items-center justify-center">
                      {sale.currency} {(sale.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                    <div className="flex-1 text-center border-r border-[#141414]/20 py-4 min-h-[80px] flex items-center justify-center">
                      <span className={cn(
                        "text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider",
                        (sale.status === 'Paid' || sale.status === 'Completed') ? "bg-green-100 text-green-600" : 
                        sale.status === 'Voided' ? "bg-red-100 text-red-600" : 
                        (sale.status === 'Returned' || sale.number?.toLowerCase().includes('/return')) ? "bg-purple-100 text-purple-600" : 
                        (sale.status === 'Awaiting Payment' || sale.status === 'Parked') ? "bg-orange-100 text-orange-600" : "bg-gray-100 text-gray-600"
                      )}>
                        {(() => {
                          const isReturn = sale.number?.toLowerCase().includes('/return');
                          if (isReturn) {
                            return sale.status === 'Paid' ? 'Returned/Paid' : 'Returned/Awaiting payment';
                          }
                          if (sale.status === 'Awaiting Payment') return 'Parked';
                          return sale.status;
                        })()}
                      </span>
                    </div>
                    <div className="flex-1 text-center py-4 min-h-[80px] flex items-center justify-center">
                      <div className="flex justify-center">
                        <select
                          value={sale.fulfillmentStatus || 'pending'}
                          onChange={async (e) => {
                            e.stopPropagation();
                            try {
                              const localToken = typeof window !== 'undefined' ? localStorage.getItem('app_auth_token') : null;
                              if (localToken) {
                                const res = await fetch(`/api/organizations/${orgId}/pos-sales/${sale.id}`, {
                                  method: 'PATCH',
                                  headers: { Authorization: `Bearer ${localToken}`, 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ fulfillmentStatus: e.target.value }),
                                });
                                if (!res.ok) throw new Error('Failed to update fulfillment status');
                                setSales(prev => prev.map(s => s.id === sale.id ? { ...s, fulfillmentStatus: e.target.value } : s));
                              } else {
                                await updateDoc(doc(db, 'documents', sale.id), {
                                  fulfillmentStatus: e.target.value,
                                  updatedAt: serverTimestamp()
                                });
                              }
                              notify.confirm('Fulfillment status updated');
                            } catch (error) {
                              handleFirestoreError(error, OperationType.UPDATE, 'documents');
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className={cn(
                            "text-[10px] px-2 py-1 rounded-lg font-bold uppercase tracking-wider border-none outline-none cursor-pointer appearance-none text-center w-[121.2px] pt-1 pb-1 pr-2 pl-2 ml-0 mr-[-75px]",
                            (sale.fulfillmentStatus || 'pending') === 'delivered' ? "bg-green-100 text-green-600" :
                            (sale.fulfillmentStatus || 'pending') === 'canceled' || (sale.fulfillmentStatus || 'pending') === 'returned' ? "bg-red-100 text-red-600" :
                            (sale.fulfillmentStatus || 'pending') === 'out for delivery' ? "bg-blue-100 text-blue-600" :
                            (sale.fulfillmentStatus || 'pending') === 'ready to pick up' ? "bg-purple-100 text-purple-600" :
                            (sale.fulfillmentStatus || 'pending') === 'in progress' ? "bg-orange-100 text-orange-600" :
                            "bg-gray-100 text-gray-600"
                          )}
                        >
                          <option value="pending">Pending</option>
                          <option value="in progress">In Progress</option>
                          <option value="ready to pick up">Ready to Pick Up</option>
                          <option value="out for delivery">Out for Delivery</option>
                          <option value="delivered">Delivered</option>
                          <option value="canceled">Canceled</option>
                          <option value="returned">Returned</option>
                        </select>
                      </div>
                    </div>
                    <div className="w-24 flex justify-center items-center gap-2 px-2">
                    {activeTab === 'return' && (
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleReturn(sale); }}
                        className="px-3 py-1 bg-[#141414] text-white text-[10px] font-bold rounded-lg hover:opacity-90 transition-all"
                      >
                        Return
                      </button>
                    )}
                    {activeTab === 'continue' && (
                      <button 
                        onClick={(e) => { e.stopPropagation(); onContinueSale?.(sale.id); }}
                        className="px-3 py-1 bg-[#141414] text-white text-[10px] font-bold rounded-lg hover:opacity-90 transition-all"
                      >
                        RO/RW
                      </button>
                    )}
                    {activeTab === 'all' && (
                      <div className="w-4 h-4" />
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedSaleId === sale.id && (
                  <div className="bg-[#141414] text-[#E4E3E0] rounded-b-[2.5rem] p-8 shadow-2xl animate-in slide-in-from-top-4 duration-300">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                      <div className="lg:col-span-2 space-y-8">
                        <div>
                          <h3 className="text-xs font-bold uppercase tracking-widest opacity-40 mb-4 pb-2 border-b border-white/10">Sale details</h3>
                          <div className="space-y-4">
                            {sale.items.map((item, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-4">
                                  <span className="w-6 text-xs opacity-40 font-bold">{item.quantity}</span>
                                  <div>
                                    <p className="font-bold">{item.description}</p>
                                    <p className="text-[10px] opacity-40">Sold by: {sale.createdByName}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-8">
                                  <p className="text-[10px] opacity-40">@ {sale.currency} {(item.unitPrice || 0).toLocaleString()} + {sale.currency} {item.taxAmount?.toLocaleString() || '0.00'} Tax</p>
                                  <p className="font-mono font-bold">{sale.currency} {(item.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-8">
                          <div className="space-y-4">
                            <h4 className="text-[10px] font-bold uppercase tracking-widest opacity-40">Note</h4>
                            <p className="text-sm italic opacity-60">{sale.notes || 'No notes for this sale.'}</p>
                          </div>
                          <div className="space-y-2 border-t border-white/10 pt-4">
                            <div className="flex justify-between text-xs opacity-40">
                              <span>Subtotal</span>
                              <span>{sale.currency} {(sale.subtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex justify-between text-xs opacity-40">
                              <span>Total tax (No Tax)</span>
                              <span>{sale.currency} {(sale.totalTax || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex justify-between text-lg font-bold italic serif pt-4 border-t border-white/10">
                              <span>SALE TOTAL</span>
                              <span>{sale.currency} {(sale.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex justify-between text-xs pt-4 opacity-60">
                              <div className="flex items-center gap-2">
                                <CreditCard className="w-3 h-3" />
                                <span>{sale.paymentMethod || 'Credit card'}</span>
                              </div>
                              <span>{(() => {
                                try {
                                  return new Date(sale.createdAt?.toDate?.() || sale.createdAt).toLocaleString();
                                } catch (e) {
                                  return 'Invalid Date';
                                }
                              })()}</span>
                              <span>{sale.currency} {(sale.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex justify-between text-xs font-bold pt-4 border-t border-white/10">
                              <span>Balance</span>
                              <span>{sale.currency} 0.00</span>
                            </div>
                          </div>
                        </div>

                        <div className="pt-8 border-t border-white/10">
                          <h4 className="text-[10px] font-bold uppercase tracking-widest opacity-40 mb-4">History & Notes</h4>
                          <HistoryNotesView orgId={orgId} entityId={sale.id} entityType="Invoice" />
                        </div>
                      </div>

                      <div className="space-y-3">
                        {activeTab === 'continue' ? (
                          <button 
                            onClick={() => onContinueSale?.(sale.id)}
                            className="w-full flex items-center justify-center gap-3 py-4 bg-primary text-white rounded-2xl font-bold hover:opacity-90 transition-all shadow-lg shadow-primary/20"
                          >
                            <ArrowRight className="w-4 h-4" />
                            RO/RW
                          </button>
                        ) : (
                          <button 
                            onClick={() => handleReturn(sale)}
                            className="w-full flex items-center justify-center gap-3 py-4 bg-white/10 text-white rounded-2xl font-bold hover:bg-white/20 transition-all border border-white/20"
                          >
                            <RotateCcw className="w-4 h-4" />
                            Return items
                          </button>
                        )}
                        <button 
                          onClick={() => setEditingSale(sale)}
                          className="w-full flex items-center justify-center gap-3 py-4 bg-white/10 text-white rounded-2xl font-bold hover:bg-white/20 transition-all border border-white/10"
                        >
                          <Edit className="w-4 h-4" />
                          Edit
                        </button>
                        <button 
                          onClick={() => handlePrintReceipt(sale, true)}
                          className="w-full flex items-center justify-center gap-3 py-4 bg-white/10 text-white rounded-2xl font-bold hover:bg-white/20 transition-all border border-white/10"
                        >
                          <Gift className="w-4 h-4" />
                          Gift receipt
                        </button>
                        <button 
                          onClick={() => handleEmailReceipt(sale)}
                          className="w-full flex items-center justify-center gap-3 py-4 bg-white/10 text-white rounded-2xl font-bold hover:bg-white/20 transition-all border border-white/10"
                        >
                          <Mail className="w-4 h-4" />
                          Email receipt
                        </button>
                        <button 
                          onClick={() => handleWhatsAppReceipt(sale)}
                          className="w-full flex items-center justify-center gap-3 py-4 bg-[#25D366]/20 text-[#25D366] rounded-2xl font-bold hover:bg-[#25D366]/30 transition-all border border-[#25D366]/30"
                        >
                          <MessageCircle className="w-4 h-4" />
                          WhatsApp receipt
                        </button>
                        <button 
                          onClick={() => handlePrintReceipt(sale, false)}
                          className="w-full flex items-center justify-center gap-3 py-4 bg-white/10 text-white rounded-2xl font-bold hover:bg-white/20 transition-all border border-white/10"
                        >
                          <Printer className="w-4 h-4" />
                          Print receipt...
                        </button>
                        <button 
                          onClick={() => handleVoid(sale.id)}
                          className="w-full flex items-center justify-center gap-3 py-4 bg-red-500/10 text-red-500 rounded-2xl font-bold hover:bg-red-500/20 transition-all border border-red-500/20"
                        >
                          <Slash className="w-4 h-4" />
                          Void
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer Stats */}
      <div className="pt-8 border-t border-[#141414]/10">
      </div>

      {/* Edit Sale Modal */}
      {editingSale && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-[#1C1C1C] text-white w-full max-w-4xl rounded-[2.5rem] overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            <div className="p-8 space-y-8">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold italic serif">Edit sale on receipt <span className="opacity-40">#{editingSale.number}</span></h2>
                  <p className="text-xs opacity-40 mt-1">Any changes made on this sale will automatically update your sales and payments reports.</p>
                </div>
                <button 
                  onClick={() => setEditingSale(null)}
                  className="p-2 hover:bg-white/5 rounded-full transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleUpdateSale} className="space-y-8">
                {/* Sale Date */}
                <div className="space-y-4">
                  <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 block">Sale date</label>
                  <div className="flex flex-wrap gap-4">
                    <div className="relative">
                      <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40" />
                      <input 
                        type="date" 
                        value={editingSale.date || ''}
                        onChange={(e) => setEditingSale({ ...editingSale, date: e.target.value })}
                        className="bg-[#2A2A2A] border border-white/5 rounded-xl pl-10 pr-4 py-3 text-sm outline-none focus:ring-2 ring-primary/20 transition-all w-48"
                      />
                    </div>
                    <div className="flex gap-2">
                      <select className="bg-[#2A2A2A] border border-white/5 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 ring-primary/20 transition-all">
                        {Array.from({ length: 12 }).map((_, i) => (
                          <option key={i} value={i + 1}>{i + 1}</option>
                        ))}
                      </select>
                      <select className="bg-[#2A2A2A] border border-white/5 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 ring-primary/20 transition-all">
                        {Array.from({ length: 60 }).map((_, i) => (
                          <option key={i} value={i.toString().padStart(2, '0')}>{i.toString().padStart(2, '0')}</option>
                        ))}
                      </select>
                      <select className="bg-[#2A2A2A] border border-white/5 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 ring-primary/20 transition-all">
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Customer */}
                <div className="space-y-4">
                  <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 block">Customer</label>
                  <div className="flex items-center justify-between bg-[#2A2A2A] p-4 rounded-2xl border border-white/5">
                    <div className="flex-1">
                      {editingSale.contactId && editingSale.contactId !== 'walk-in' ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold">
                              {editingSale.contactName?.split(' ').map(n => n[0]).join('').toUpperCase() || '??'}
                            </div>
                            <div>
                              <p className="font-bold">{editingSale.contactName}</p>
                              <p className="text-[10px] opacity-40">{editingSale.contactId}</p>
                            </div>
                          </div>
                          <button 
                            type="button"
                            onClick={() => setEditingSale({ ...editingSale, contactName: 'Walk-in Customer', contactId: 'walk-in' })}
                            className="p-2 hover:bg-red-500/10 text-red-400 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      ) : (
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40" />
                          <select 
                            value={editingSale.contactId || 'walk-in'}
                            onChange={(e) => {
                              const contact = contacts.find(c => c.id === e.target.value);
                              setEditingSale({ 
                                ...editingSale, 
                                contactId: e.target.value, 
                                contactName: contact ? contact.name : 'Walk-in Customer' 
                              });
                            }}
                            className="w-full bg-transparent border-none pl-10 pr-4 py-2 text-sm outline-none focus:ring-0 appearance-none cursor-pointer font-bold"
                          >
                            <option value="walk-in" className="bg-[#1C1C1C]">Walk-in Customer</option>
                            {contacts.map(contact => (
                              <option key={contact.id} value={contact.id} className="bg-[#1C1C1C]">
                                {contact.name} {contact.phone ? `(${contact.phone})` : ''}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40 pointer-events-none" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Sale Note */}
                <div className="space-y-4">
                  <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 block">Sale note</label>
                  <div className="relative">
                    <textarea 
                      placeholder="Enter a note for this sale"
                      value={editingSale.notes || ''}
                      onChange={(e) => setEditingSale({ ...editingSale, notes: e.target.value })}
                      maxLength={2000}
                      className="w-full bg-[#2A2A2A] border border-white/5 rounded-2xl p-4 text-sm outline-none focus:ring-2 ring-primary/20 transition-all min-h-[120px] resize-none"
                    />
                    <span className="absolute bottom-4 right-4 text-[10px] opacity-20 font-bold">2000 characters max</span>
                  </div>
                </div>

                {/* Payment Section */}
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-8">
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Payment type</label>
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Date</label>
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 text-right">Amount</label>
                  </div>
                  <div className="grid grid-cols-3 gap-4 items-center">
                    <select 
                      value={editingSale.paymentMethod || ''}
                      onChange={(e) => setEditingSale({ ...editingSale, paymentMethod: e.target.value })}
                      className="bg-[#2A2A2A] border border-white/5 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 ring-primary/20 transition-all"
                    >
                      <option value="">Select Payment Method</option>
                      {bankAccounts.map(acc => (
                        <option key={acc.id} value={acc.accountName}>{acc.accountName}</option>
                      ))}
                      {chartOfAccounts.filter(coa => coa.enablePayments || coa.type === 'Bank').map(coa => (
                        <option key={coa.id} value={coa.name}>{coa.name}</option>
                      ))}
                    </select>
                    <div className="flex gap-2">
                      <input 
                        type="date" 
                        value={editingSale.date || ''}
                        onChange={(e) => setEditingSale({ ...editingSale, date: e.target.value })}
                        className="bg-[#2A2A2A] border border-white/5 rounded-xl px-4 py-3 text-xs outline-none focus:ring-2 ring-primary/20 transition-all w-full"
                      />
                      <select className="bg-[#2A2A2A] border border-white/5 rounded-xl px-2 py-3 text-xs outline-none">
                        <option>9</option>
                      </select>
                      <select className="bg-[#2A2A2A] border border-white/5 rounded-xl px-2 py-3 text-xs outline-none">
                        <option>40</option>
                      </select>
                      <select className="bg-[#2A2A2A] border border-white/5 rounded-xl px-2 py-3 text-xs outline-none">
                        <option>PM</option>
                      </select>
                    </div>
                    <div className="relative flex justify-end">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-bold opacity-40">{editingSale.currency}</span>
                      <input 
                        type="text" 
                        readOnly
                        value={(editingSale.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        style={{ width: `${(editingSale.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }).length + 5}ch` }}
                        className="bg-[#2A2A2A] border border-white/5 rounded-xl pl-10 pr-4 py-3 text-sm font-mono font-bold text-right outline-none opacity-50"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <button 
                    type="submit"
                    disabled={isSaving}
                    className="bg-primary text-white px-12 py-4 rounded-2xl font-bold text-sm shadow-xl shadow-primary/20 hover:opacity-90 transition-all disabled:opacity-50 flex items-center gap-2"
                  >
                    {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                    Save changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
